import React from 'react';
import styled from '@emotion/styled';
import { useStaticQuery, graphql, withPrefix } from 'gatsby';
import { navigate } from '@reach/router';

import {
  GraphQLNodeType,
  InitiativesModel,
  deliverablesRowPropsModel,
} from '../types';
import { LinesEllipsis } from './LinesEllipsis';

const Deliverable = styled.div`
  display: grid;
  justify-content: center;
  justify-items: center;
  grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
  gap: 1.5rem 1rem;
  padding: 1rem 0;
`;

const DeliverableItem = styled.div`
  width: 350px;
  text-align: center;
  height: 190px;
  border-radius: 0.375rem;
  background-color: #ffffff;
  box-shadow: 0 4px 0.3125rem 0 rgba(0, 0, 0, 0.06);
  background-color: #fff;
  text-align: left;

  .DeliverableInitiativeName {
    display: flex;
    align-items: center;
    height: 40px;
    padding: 1rem;
    font-weight: bold;
    text-align: left;
  }

  .DeliverableInitiativeLink {
    height: 70px;
    padding: 0 1rem;
    text-align: left;
  }
`;

const DeliverableItemType = styled.div`
  display: flex;
  align-items: center;
  margin-top: 0.5rem;
  width: 100%;
  text-align: left;
`;

const DeliverableSubmit = styled.div`
  width: 100%;
  text-align: center;
`;

const PageLink = styled.a`
  color: ${(props) => props.theme.palette.primary.main};
  font-size: 1rem;
  font-weight: bold;
  text-decoration: underline;
  width: 100%;
  text-align: left;
`;

const formatsIcon = [
  {
    name: 'Fichier à télécharger',
    url: '/assets/file-download.svg',
  },
  {
    name: 'Page web',
    url: '/assets/webpage.svg',
  },
  {
    name: 'Podcast',
    url: '/assets/podcast.svg',
  },
  {
    name: 'Site web/Application',
    url: '/assets/app.svg',
  },
  {
    name: 'Video',
    url: '/assets/video.svg',
  },
  {
    name: 'Autre type de livrable que je vais préciser',
    url: '/assets/app.svg',
  },
];

const mergeDeliverables = (
  deliverables,
  deliverablesName,
  deliverablesFormat,
  deliverablesSubmitDate,
) => {
  let count = deliverables.length;
  if (
    deliverablesName.length === count &&
    deliverablesFormat.length === count &&
    deliverablesSubmitDate.length === count
  ) {
    return deliverables.map((v, idx) => {
      let icon = formatsIcon.find((i) => i.name === deliverablesFormat[idx]);

      return {
        name: deliverablesName[idx],
        value: v,
        format: deliverablesFormat[idx],
        submitDate: deliverablesSubmitDate[idx],
        icon: icon?.url ?? '/assets/app.svg',
      };
    });
  } else {
    console.warn('Deliverables are inconsistent');
    console.log(
      'DEBUG',
      deliverables,
      deliverablesName,
      deliverablesFormat,
      deliverablesSubmitDate,
    );
    return [];
  }
};

const DeliverableFilterLink = styled.div`
  text-decoration: underline;
  width: 100%;
  text-align: center;
  margin-top: 1rem;
  cursor: pointer;

  :hover {
    color: #41cbab;
  }
`;

type InitiativesDeliverablesType = Pick<
  InitiativesModel,
  | 'IDInitiative'
  | 'NomInitiative'
  | 'DateCreationInitiative'
  | 'EtatAffiche2'
  | 'IntitulesLivrable'
  | 'LiensLivrable'
  | 'FormatsLivrable'
  | 'DateSoumissionLivrables'
>;

type deliverablesTypes = {
  InitiativeListDeliverables: GraphQLNodeType<InitiativesDeliverablesType>;
};

const DeliverablesRow = (props: deliverablesRowPropsModel) => {
  const deliverablesRowNumber = props.number;

  const data_query = useStaticQuery<deliverablesTypes>(graphql`
    query InitiativeListDeliverablesQuery {
      InitiativeListDeliverables: allAirtable(
        filter: { table: { eq: "Initiatives" } }
        sort: { data: { DateSoumissionLivrables: DESC } }
      ) {
        edges {
          node {
            data {
              IDInitiative
              NomInitiative
              DateCreationInitiative
              EtatAffiche2
              IntitulesLivrable
              LiensLivrable
              FormatsLivrable
              DateSoumissionLivrables
            }
          }
        }
      }
    }
  `);

  const initiatives = data_query?.InitiativeListDeliverables?.edges?.map(
    (item) => {
      const data = item?.node?.data;
      const initiative = {
        id: data?.IDInitiative,
        name: data?.NomInitiative,
        creationDate: data?.DateCreationInitiative,
        // FIXME Status behavior differ slightly from types and thematiques,
        // we might want to harmonise them
        status: data?.EtatAffiche2?.[0],
        deliverables: data?.IntitulesLivrable ?? [],
        deliverablesName: data?.LiensLivrable ?? [],
        deliverablesFormat: data?.FormatsLivrable ?? [],
        deliverablesSubmitDate: data?.DateSoumissionLivrables ?? [],
      };

      return initiative;
    },
  );

  const start = 0;
  const end = deliverablesRowNumber;

  let filteredData = initiatives;

  filteredData = filteredData.filter((i) => i.deliverables.length != 0);

  const displayInitiatives = filteredData.slice(start, end);

  return (
    <>
      <div>
        {filteredData.length} {'initiatives avec des travaux'}
      </div>
      <Deliverable>
        {displayInitiatives.map((initiative) => {
          const mergedDeliverables = mergeDeliverables(
            initiative.deliverables,
            initiative.deliverablesName,
            initiative.deliverablesFormat,
            initiative.deliverablesSubmitDate,
          );
          return mergedDeliverables.map((deliverable, idx) => {
            if (idx == 0) {
              return (
                <DeliverableItem key={idx}>
                  <div className="DeliverableInitiativeName">
                    <LinesEllipsis>{initiative.name}</LinesEllipsis>
                  </div>
                  <div className="DeliverableInitiativeLink">
                    <PageLink
                      href={deliverable.name}
                      target="_blank"
                      rel="noreferrer"
                    >
                      <LinesEllipsis>{deliverable.value}</LinesEllipsis>
                    </PageLink>
                    <DeliverableItemType>
                      <img
                        style={{ marginRight: '10px' }}
                        src={deliverable.icon}
                        height={18}
                        width={18}
                        alt={deliverable.format}
                      />
                      {deliverable.format}
                    </DeliverableItemType>
                  </div>
                  <DeliverableSubmit>
                    <div style={{ paddingTop: '0.5rem' }}>
                      <a
                        href={withPrefix(
                          `/initiative/${initiative.id}#travaux`,
                        )}
                      >
                        Voir tous les travaux
                      </a>
                    </div>
                  </DeliverableSubmit>
                </DeliverableItem>
              );
            }
          });
        })}
      </Deliverable>
      <DeliverableFilterLink
        onClick={() => {
          navigate(withPrefix(`/initiatives/?initiative=travaux`));
        }}
      >
        Voir toutes les initiatives avec des travaux publiés
      </DeliverableFilterLink>
    </>
  );
};

export default DeliverablesRow;
